import type { Client, ClientHeaders } from "@medusajs/js-sdk";
import { z } from "zod";

/**
 * Client for the **supplier catalog** store route the order assistant's
 * read-only detail view uses (KAN-382):
 * `GET /store/supplier-catalog/products/:id`, where `:id` is the supplier
 * product id (`sprod_...`).
 *
 * Only the public view of a supplier product is returned: nothing that exists
 * in a trade-only view, and only variants that are public and available (so the
 * response carries no availability flag). `trade_price` is present only for a
 * salon with a claimed trade account. There is no external product URL, by
 * design, and no currency: the caller already knows it from the card.
 */

const variantSchema = z.object({
  supplier_variant_id: z.string(),
  variant_title: z.string().nullable(),
  option_values: z.array(z.object({ name: z.string(), value: z.string() })),
  sku: z.string().nullable(),
  ean: z.string().nullable(),
  /** Decimal major units; `null` when the supplier shows no price. */
  public_price: z.number().nullable(),
  /**
   * The image tagged for the variant, else the product's first. Absent on an
   * older api, then the gallery's first image is used.
   */
  image: z.string().nullable().optional(),
  /** The key is absent unless the salon has a claimed trade account. */
  trade_price: z.number().optional()
});

const productSchema = z.object({
  supplier_product_id: z.string(),
  title: z.string(),
  brand: z.string().nullable(),
  /** Sanitised plain text. */
  description: z.string().nullable(),
  /** Gallery image URLs. They are supplier-hosted: render without a referrer. */
  images: z.array(z.string()),
  options: z.array(z.object({ name: z.string(), values: z.array(z.string()) })),
  seller_id: z.string(),
  seller_name: z.string(),
  store_host: z.string(),
  variants: z.array(variantSchema)
});

const responseSchema = z.object({ product: productSchema });

export type StoreSupplierProductVariant = z.infer<typeof variantSchema>;
export type StoreSupplierProduct = z.infer<typeof productSchema>;

export class SupplierCatalog {
  private client: Client;

  constructor(client: Client) {
    this.client = client;
  }

  /**
   * A supplier product's public detail. Rejects with a 404 `FetchError` when
   * the product is unknown, trade-only, or has no public available variant, and
   * with a plain `Error` when the body is not the shape KAN-382 documents.
   */
  product = async (
    id: string,
    options?: { signal?: AbortSignal; headers?: ClientHeaders }
  ): Promise<StoreSupplierProduct> => {
    const body = await this.client.fetch<unknown>(
      `/store/supplier-catalog/products/${encodeURIComponent(id)}`,
      { method: "GET", headers: options?.headers, signal: options?.signal }
    );
    const parsed = responseSchema.safeParse(body);
    if (!parsed.success) {
      throw new Error("Unexpected supplier product response");
    }
    return parsed.data.product;
  };
}
